import React from "react";
import { filter, get, isEmpty, join } from "lodash";
import { Box, Flex, H5, P } from "orcs-design-system";
import PropTypes from "prop-types";
import { copywriting } from "src/pages/PersonDetailPage/PersonDetailPage.config";
import { fullLegalName, isValidPerson } from "src/util/person";
import { HIERARCHY_SEPARATOR, getSupplyBadgeGroups } from "src/util/group";
import Bands from "src/components/PersonDetails/Bands";
import PersonItem from "src/components/Person/PersonItem";
import { useAuth0 } from "src/contexts/auth0Provider";
import {
  useUserPerson,
  useAllocationProject,
} from "src/contexts/global/GlobalContext";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import useRestricted from "src/app/Restricted/useRestricted";

const Division = ({ division }) => {
  if (!division) {
    return null;
  }

  const { hierarchy } = division;
  const paths = join(filter(hierarchy.split(HIERARCHY_SEPARATOR)), " > ");

  return <P>{paths}</P>;
};

Division.propTypes = {
  division: PropTypes.object,
};

export const Details = ({ person }) => {
  const { user } = useAuth0();
  const userPerson = useUserPerson();
  const allocationProject = useAllocationProject();
  const workspace = useWorkspace();
  const [isDirectoryUser] = useRestricted();

  const managingPerson = get(person, "managedBy");
  const showManagingPerson = isValidPerson(managingPerson);

  const workspaceFeatureFlags = get(workspace, "config.featureFlags", {});
  const groupTypes = get(workspace, "config.groupTypes");
  const hideDirectReports = get(
    workspace,
    "config.allocation.hideDirectReports"
  );

  const showReportsTo = showManagingPerson && !hideDirectReports;
  const showEmployeeId =
    workspaceFeatureFlags &&
    !workspaceFeatureFlags.hideEmployeeIdOnPersonDetails;

  const isViewingOwnProfile = userPerson?.aggregateId === person?.aggregateId;
  const showFullName =
    (!isDirectoryUser || isViewingOwnProfile) &&
    (person?.firstName || person?.surname);

  const personBands = get(person, "bands", []);
  const hasBands = !isEmpty(personBands);
  const showBands = hasBands && !isDirectoryUser;

  // Match PersonDetails visibility controls - only show to power/admin users
  const showExtra = !isDirectoryUser;

  const featureFlags = get(workspace, "config.featureFlags", {});

  const ManagedByDetails = showManagingPerson ? (
    <PersonItem
      reportsTo
      isDisplayActionsButton={false}
      person={managingPerson}
      badgeGroups={getSupplyBadgeGroups({
        person: managingPerson,
        groupTypes,
      })}
      groupTypes={groupTypes}
      user={user}
      workspace={workspace}
      allocationProject={allocationProject}
      featureFlags={featureFlags}
      userPerson={userPerson}
    />
  ) : null;

  return (
    <Flex flexDirection="column" gap="r">
      {showFullName && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.Full_name}
          </H5>
          <P>{fullLegalName(person)}</P>
        </Box>
      )}
      {showReportsTo && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.REPORTS_TO}
          </H5>
          {ManagedByDetails}
        </Box>
      )}
      {person?.company && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.EMPLOYED_BY}
          </H5>
          <P>{person.company}</P>
        </Box>
      )}
      {person?.division && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.DIVISION}
          </H5>
          <Division division={person.division} />
        </Box>
      )}
      {showEmployeeId && showExtra && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.Employee_ID}
          </H5>
          <P>{get(person, "customerEmployeeId") || "No Employee ID"}</P>
        </Box>
      )}
      {showBands && (
        <Box>
          <H5 sizing="small" weight="bold" color="greyDark" uppercase>
            {copywriting.Band}
          </H5>
          <Bands person={person} />
        </Box>
      )}
    </Flex>
  );
};

Details.propTypes = {
  person: PropTypes.object,
};
